import { APP_NAME } from '@trips/shared/config';
import { formatFullDate } from '@trips/shared/format';
import { newId } from '@trips/shared/ids';
import type { Place, Settings } from '@trips/shared/schema';
import { useCallback, useEffect, useState } from 'react';
import { patch, put } from '../../data/mutate';
import { hasSampleTrips, loadSampleTrips, removeSampleTrips } from '../../data/sample';
import { selectHome, selectPrefs, selectSettings, selectToday } from '../../data/selectors';
import { isDemo, leaveSampleMode, signOut } from '../../data/session';
import { getState, useStore } from '../../data/store';
import { syncNow } from '../../data/sync';
import { api } from '../../lib/api';
import { startupTimes } from '../../lib/perf';
import { appVersion, buildTime, isNative } from '../../lib/platform';
import { QrCode } from '../../lib/qr';
import { navigate } from '../../lib/router';
import { checkForUpdate, installUpdate, type Latest } from '../../lib/update';
import { Button } from '../../ui/Button';
import { Select, TextField } from '../../ui/Field';
import { ListGroup, ListRow } from '../../ui/List';
import { Page } from '../../ui/Page';
import { Segmented } from '../../ui/Segmented';
import { toast } from '../../ui/Toast';
import { FriendsPage } from '../friends/FriendsPage';
import { AirportField, PlaceField } from '../plan/Pickers';
import { ClaudeImportPage, IMPORT_ADDRESS } from './ClaudeImportPage';
import { NotificationsPage } from './NotificationsPage';
import s from './SettingsScreen.module.css';
import { WalletImportPage } from './WalletImportPage';

function setSetting<K extends keyof Settings>(key: K, value: Settings[K]) {
  const existing = getState().tables.settings.get('settings');
  if (existing) void patch('settings', 'settings', { [key]: value } as Partial<Settings>);
  else void put('settings', { ...selectSettings(getState()), [key]: value });
}

function Display() {
  const st = useStore(selectSettings);
  return (
    <ListGroup title="Display">
      <div className={s.control}>
        <span className={s.controlLabel}>Distance</span>
        <Segmented
          label="Distance"
          value={st.units}
          onChange={(v) => setSetting('units', v)}
          options={[
            { value: 'mi', label: 'Miles' },
            { value: 'km', label: 'Kilometers' },
          ]}
        />
      </div>
      <div className={s.control}>
        <span className={s.controlLabel}>Time</span>
        <Segmented
          label="Time"
          value={st.timeFormat}
          onChange={(v) => setSetting('timeFormat', v)}
          options={[
            { value: '12h', label: '7:05 PM' },
            { value: '24h', label: '19:05' },
          ]}
        />
      </div>
      <div className={s.control}>
        <span className={s.controlLabel}>Dates</span>
        <Segmented
          label="Dates"
          value={st.dateFormat}
          onChange={(v) => setSetting('dateFormat', v)}
          options={[
            { value: 'us', label: 'Oct 10' },
            { value: 'intl', label: '10 Oct' },
          ]}
        />
      </div>
      <div className={s.control}>
        <span className={s.controlLabel}>Theme</span>
        <Segmented
          label="Theme"
          value={st.theme}
          onChange={(v) => setSetting('theme', v)}
          options={[
            { value: 'system', label: 'System' },
            { value: 'light', label: 'Light' },
            { value: 'dark', label: 'Dark' },
          ]}
        />
      </div>
    </ListGroup>
  );
}

function TripRules() {
  const st = useStore(selectSettings);
  return (
    <ListGroup
      title="Trips"
      footer="Used to group plans into trips automatically when bookings start arriving by email."
    >
      <div className={s.inline}>
        <Select
          label="Away from home after"
          value={String(st.tripRadiusMi)}
          onChange={(v) => setSetting('tripRadiusMi', Number(v))}
          options={[25, 50, 75, 100, 150].map((n) => ({ value: String(n), label: `${n} mi` }))}
        />
        <Select
          label="Keep gaps together up to"
          value={String(st.tripGapDays)}
          onChange={(v) => setSetting('tripGapDays', Number(v))}
          options={[0, 1, 2, 3, 5, 7].map((n) => ({
            value: String(n),
            label: n === 1 ? '1 day' : `${n} days`,
          }))}
        />
      </div>
    </ListGroup>
  );
}

function HomeBasePage() {
  const home = useStore(selectHome);
  const today = useStore(selectToday);
  const prefs = useStore(selectPrefs);
  const homeBases = useStore((st) => st.tables.homeBases);
  const places = useStore((st) => st.tables.places);
  const [mode, setMode] = useState<'airport' | 'city'>('airport');
  const [place, setPlace] = useState<Place | null>(null);
  const [from, setFrom] = useState(today);
  const history = [...homeBases.values()]
    .filter((h) => !h.deleted)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));

  return (
    <Page title="Home base" backTo="/settings">
      <p className="muted">
        Trips are measured from here. Keeping the history means older trips still count from where
        you lived then.
      </p>
      <ListGroup title="History">
        {history.map((h) => {
          const p = places.get(h.placeId);
          return (
            <ListRow
              key={h.id}
              icon="house"
              title={p ? `${p.city ?? p.name}${p.iata ? ` (${p.iata})` : ''}` : 'Unknown place'}
              detail={
                h.effectiveFrom <= '1971-01-01'
                  ? 'From the start'
                  : `From ${formatFullDate(h.effectiveFrom, prefs)}`
              }
              trailing={
                h.id === home?.base.id ? (
                  <span className="t-caption muted">Current</span>
                ) : undefined
              }
            />
          );
        })}
      </ListGroup>
      <div className={s.form}>
        <h2 className="t-headline">Move your home base</h2>
        <Segmented
          label="Search by"
          value={mode}
          onChange={setMode}
          options={[
            { value: 'airport', label: 'Airport' },
            { value: 'city', label: 'City or address' },
          ]}
        />
        {mode === 'airport' ? (
          <AirportField
            label="Nearest airport"
            value={place?.kind === 'airport' ? place : null}
            onChange={setPlace}
          />
        ) : (
          <PlaceField
            label="City or address"
            value={place?.kind === 'city' ? place : null}
            onChange={setPlace}
            kind="city"
          />
        )}
        <TextField label="Starting" type="date" value={from} onChange={setFrom} />
        <Button
          variant="primary"
          disabled={!place || !from}
          onClick={async () => {
            if (!place) return;
            await put('homeBases', { id: newId('hb'), placeId: place.id, effectiveFrom: from });
            toast('Home base updated');
            setPlace(null);
          }}
        >
          Save home base
        </Button>
      </div>
    </Page>
  );
}

interface DeviceRow {
  id: string;
  name: string;
  platform: string;
  created_at: number;
  last_seen_at: number | null;
}

function DevicesPage() {
  const prefs = useStore(selectPrefs);
  const [devices, setDevices] = useState<DeviceRow[] | null>(null);
  const [pair, setPair] = useState<{ code: string; qr: string; expiresAt: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api<{ devices: DeviceRow[] }>('/api/devices')
        .then((r) => setDevices(r.devices))
        .catch((e) => setError((e as Error).message)),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <Page title="Devices" backTo="/settings">
      {!isNative && (
        <div className={s.pair}>
          {pair ? (
            <>
              <QrCode text={pair.qr} label="Pairing code" />
              <div className={s.pairText}>
                <h2 className="t-headline">Scan this with the Android app</h2>
                <p className="muted">
                  Open {APP_NAME} on your phone and tap Scan code. Or type{' '}
                  <strong className="num">{pair.code}</strong>. The code works once, for 10 minutes.
                </p>
                <Button onClick={() => void load()}>I've paired it</Button>
              </div>
            </>
          ) : (
            <div className={s.pairText}>
              <h2 className="t-headline">Pair your phone</h2>
              <p className="muted">
                The Android app signs in by scanning a one-time code from here. You can remove a
                phone at any time.
              </p>
              <Button
                variant="primary"
                icon="qr-code"
                onClick={() =>
                  api<{ code: string; qr: string; expiresAt: number }>('/api/devices/pair-code', {
                    body: {},
                  })
                    .then(setPair)
                    .catch((e) => toast((e as Error).message, { tone: 'error' }))
                }
              >
                Show pairing code
              </Button>
            </div>
          )}
        </div>
      )}
      <ListGroup title="Paired devices">
        {devices?.length ? (
          devices.map((d) => (
            <ListRow
              key={d.id}
              icon="device-mobile"
              title={d.name}
              detail={`Paired ${formatFullDate(new Date(d.created_at).toISOString().slice(0, 10), prefs)}`}
              trailing={
                <Button
                  size="sm"
                  variant="danger"
                  onClick={async () => {
                    await api(`/api/devices/${d.id}`, { method: 'DELETE' });
                    toast(`${d.name} removed`);
                    void load();
                  }}
                >
                  Remove
                </Button>
              }
            />
          ))
        ) : (
          <ListRow title={error ?? (devices ? 'No phones paired yet' : 'Loading')} />
        )}
      </ListGroup>
    </Page>
  );
}

function PasskeysPage() {
  const [keys, setKeys] = useState<
    { id: string; name: string; created_at: number; last_used_at: number | null }[] | null
  >(null);
  const load = useCallback(
    () =>
      api<{ passkeys: typeof keys }>('/api/auth/passkeys')
        .then((r) => setKeys(r.passkeys))
        .catch(() => setKeys([])),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);
  return (
    <Page title="Passkeys" backTo="/settings">
      <p className="muted">
        Sign in on a computer with Windows Hello or a password manager. Your password and
        authenticator code always work too.
      </p>
      <ListGroup>
        {(keys ?? []).map((k) => (
          <ListRow
            key={k.id}
            icon="fingerprint"
            title={k.name}
            detail={
              k.last_used_at
                ? `Last used ${new Date(k.last_used_at).toLocaleDateString()}`
                : 'Not used yet'
            }
            trailing={
              <Button
                size="sm"
                variant="danger"
                onClick={async () => {
                  await api(`/api/auth/passkeys/${encodeURIComponent(k.id)}`, { method: 'DELETE' });
                  void load();
                }}
              >
                Remove
              </Button>
            }
          />
        ))}
        {keys?.length === 0 && <ListRow title="No passkeys yet" />}
      </ListGroup>
      <Button
        variant="primary"
        icon="fingerprint"
        onClick={async () => {
          try {
            const { startRegistration } = await import('@simplewebauthn/browser');
            const opts = await api<{
              challengeId: string;
              options: Parameters<typeof startRegistration>[0]['optionsJSON'];
            }>('/api/auth/passkeys/options', { body: {} });
            const response = await startRegistration({ optionsJSON: opts.options });
            await api('/api/auth/passkeys', { body: { challengeId: opts.challengeId, response } });
            toast('Passkey added');
            void load();
          } catch (e) {
            toast((e as Error).message || "The passkey wasn't added.", { tone: 'error' });
          }
        }}
      >
        Add a passkey
      </Button>
    </Page>
  );
}

function SampleData() {
  const has = useStore(() => hasSampleTrips());
  const [busy, setBusy] = useState(false);
  return (
    <ListGroup
      title="Sample trips"
      footer="Adds made-up trips around a Minneapolis trip like yours, so you can try the design. Remove them in one tap."
    >
      {has ? (
        <ListRow
          icon="trash-simple"
          danger
          title={busy ? 'Removing' : 'Remove sample trips'}
          onClick={async () => {
            setBusy(true);
            await removeSampleTrips();
            setBusy(false);
            toast('Sample trips removed');
          }}
        />
      ) : (
        <ListRow
          icon="suitcase-rolling"
          title={busy ? 'Adding' : 'Add sample trips'}
          onClick={async () => {
            setBusy(true);
            await loadSampleTrips();
            setBusy(false);
            toast('Sample trips added');
          }}
        />
      )}
    </ListGroup>
  );
}

/** Android: whether a newer build is waiting, and installing it. */
function AppUpdateRow() {
  const [latest, setLatest] = useState<Latest | null | undefined>(undefined);
  const [progress, setProgress] = useState<number | null>(null);
  useEffect(() => {
    void checkForUpdate().then(setLatest);
  }, []);
  return (
    <ListRow
      title="App update"
      detail={latest ? `Version ${latest.versionName} is ready` : undefined}
      value={
        progress != null
          ? `${Math.round(progress * 100)}%`
          : latest === undefined
            ? 'Checking'
            : latest
              ? 'Update'
              : 'Up to date'
      }
      chevron={!!latest}
      onClick={
        latest && progress == null
          ? async () => {
              setProgress(0);
              try {
                if ((await installUpdate(latest, setProgress)) === 'needs-permission')
                  toast('Allow installs from Trips, then tap Update again');
              } catch (e) {
                toast(e instanceof Error ? e.message : "Couldn't update");
              } finally {
                setProgress(null);
              }
            }
          : undefined
      }
    />
  );
}

function About() {
  const sync = useStore((st) => st.sync);
  const times = startupTimes();
  return (
    <ListGroup title="About">
      <ListRow title={APP_NAME} value={`Version ${appVersion}`} />
      {isNative && <AppUpdateRow />}
      <ListRow title="Built" value={new Date(buildTime).toLocaleString()} />
      <ListRow
        title="Opened in"
        value={times.firstPaintMs != null ? `${times.firstPaintMs} ms` : 'Measuring'}
        detail={times.hydratedMs != null ? `Local data ready in ${times.hydratedMs} ms` : undefined}
      />
      <ListRow
        title="Sync"
        detail={sync.error ?? undefined}
        value={
          sync.state === 'idle'
            ? sync.pending
              ? `${sync.pending} waiting`
              : 'Up to date'
            : sync.state === 'offline'
              ? 'Offline'
              : sync.state === 'syncing'
                ? 'Syncing'
                : "Couldn't sync"
        }
        onClick={() => void syncNow()}
        chevron={false}
      />
    </ListGroup>
  );
}

export function SettingsScreen({ page }: { page: string | null }) {
  const home = useStore(selectHome);
  if (page === 'home') return <HomeBasePage />;
  if (page === 'devices') return <DevicesPage />;
  if (page === 'passkeys') return <PasskeysPage />;
  if (page === 'wallet-import') return <WalletImportPage />;
  if (page === 'claude') return <ClaudeImportPage />;
  if (page === 'notifications') return <NotificationsPage />;
  if (page === 'friends') return <FriendsPage />;
  return (
    <Page title="Settings" backTo="/">
      <ListGroup title="Home base">
        <ListRow
          icon="house"
          title={
            home
              ? `${home.place.city ?? home.place.name}${home.place.iata ? ` (${home.place.iata})` : ''}`
              : 'Set your home base'
          }
          detail={home ? home.place.name : 'Trips are measured from here'}
          to="/settings/home"
        />
      </ListGroup>
      <ListGroup title="Flights">
        <ListRow
          icon="bell"
          title="Flight alerts"
          detail="Delays, gates, cancellations, connections"
          to="/settings/notifications"
        />
        <ListRow
          icon="users"
          title="Friends"
          detail="See each other's flight maps and stats"
          to="/settings/friends"
        />
      </ListGroup>
      <Display />
      <TripRules />
      <ListGroup title="Bookings by email">
        <ListRow
          icon="envelope-simple"
          title="Claude & email import"
          detail={`Forward to ${IMPORT_ADDRESS}, or connect Claude`}
          to="/settings/claude"
        />
        <ListRow icon="tray" title="Inbox" to="/inbox" />
      </ListGroup>
      <ListGroup title="Wallet">
        <ListRow icon="credit-card" title="Import from Claude" to="/settings/wallet-import" />
      </ListGroup>
      <ListGroup title="Sign-in and devices">
        {!isNative && <ListRow icon="fingerprint" title="Passkeys" to="/settings/passkeys" />}
        <ListRow
          icon="device-mobile"
          title={isNative ? 'Paired devices' : 'Pair a phone'}
          to="/settings/devices"
        />
        {!isNative && (
          <ListRow
            icon="device-mobile"
            title="Download the Android app"
            detail="Open this on your phone, then install the file"
            onClick={() => {
              location.href = '/api/app/android';
            }}
          />
        )}
      </ListGroup>
      {!isDemo() && <SampleData />}
      <About />
      {__PREVIEW__ ? null : isDemo() ? (
        <Button variant="line" icon="sign-out" onClick={() => leaveSampleMode()}>
          Leave sample mode
        </Button>
      ) : (
        <Button
          variant="line"
          icon="sign-out"
          onClick={() => void signOut().then(() => navigate('/', { replace: true }))}
        >
          Sign out
        </Button>
      )}
    </Page>
  );
}
